import { socket } from "@/_lib/socket";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import useDebounce from "@/hooks/useDebounce";
import { ApiError } from "@/services/api/auth/auth.type";
import { useGetCampaignsDashboardApiCallQuery } from "@/services/api/campaigns/campaigns.api";
import {
  useGetTransactionsApiCallQuery,
  useLazyGetTransactionsApiCallQuery,
} from "@/services/api/transactions/transactions.api";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  Download,
  ExternalLink,
  CheckCircle,
  Clock,
  Coins,
  Filter,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from "@/components/Loader";

const dummyTransactions = [
  {
    _id: "txn_001",
    transactionType: "CLAIMED",
    hash: "0xabc123def456",
    from: "0x111111111111111",
    walletAddress: "0x999999999999999",
    amount: 100,
    status: "SUCCESS",
    totalReward: 50,
    allTasks: 10,
    allVerifiedTasks: 8,
    isClaimed: true,
    isCompleted: true,
    createdAt: "2025-08-23T10:15:00Z",
  },
  {
    _id: "txn_002",
    transactionType: "PENDING",
    hash: "0xdef456ghi789",
    from: "0x222222222222222",
    walletAddress: "0x888888888888888",
    amount: 250,
    status: "PENDING",
    totalReward: 120,
    allTasks: 20,
    allVerifiedTasks: 15,
    isClaimed: false,
    isCompleted: false,
    createdAt: "2025-08-22T14:30:00Z",
  },
  {
    _id: "txn_003",
    transactionType: "CLAIMED",
    hash: "0xghi789jkl012",
    from: "0x333333333333333",
    walletAddress: "0x777777777777777",
    amount: 75,
    status: "FAILED",
    totalReward: 30,
    allTasks: 5,
    allVerifiedTasks: 2,
    isClaimed: false,
    isCompleted: false,
    createdAt: "2025-08-21T09:45:00Z",
  },
];

export default function ClaimHistory() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const debounceSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: transactionsData,
    isError,
    isLoading,
  } = useGetTransactionsApiCallQuery({
    search: debounceSearchTerm || "",
    status: "",
    transactionType: "CLAIMED",
  });

  const [getTransation] = useLazyGetTransactionsApiCallQuery();

  const { data: campaignsData } = useGetCampaignsDashboardApiCallQuery();

  const claimHistory = [
    {
      id: 1,
      campaign: "BINGOLD Genesis Launch",
      amount: "500",
      status: "completed",
      date: "2024-01-15",
      txHash: "0x1234567890abcdef1234567890abcdef12345678",
      claimedAt: "14:32 UTC",
    },
    {
      id: 2,
      campaign: "Community Builder Campaign",
      amount: "250",
      status: "completed",
      date: "2024-01-12",
      txHash: "0xabcdef1234567890abcdef1234567890abcdef12",
      claimedAt: "09:15 UTC",
    },
    {
      id: 3,
      campaign: "Early Adopter Rewards",
      amount: "400",
      status: "pending",
      date: "2024-01-18",
      txHash: null,
      claimedAt: null,
    },
    {
      id: 4,
      campaign: "Referral Program",
      amount: "150",
      status: "processing",
      date: "2024-01-10",
      txHash: "0x9876543210fedcba9876543210fedcba98765432",
      claimedAt: "16:45 UTC",
    },
  ];

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return {
          icon: CheckCircle,
          text: "Completed",
          color: "text-green-400",
          bgColor: "bg-green-500/20",
        };
      case "processing":
        return {
          icon: Clock,
          text: "PROCESSING",
          color: "text-yellow-400",
          bgColor: "bg-yellow-500/20",
        };
      case "pending":
        return {
          icon: Clock,
          text: "PENDING",
          color: "text-gray-400",
          bgColor: "bg-gray-500/20",
        };
      default:
        return {
          icon: Clock,
          text: "Unknown",
          color: "text-gray-400",
          bgColor: "bg-gray-500/20",
        };
    }
  };

  const exportCSV = async () => {
    try {
      setIsExporting(true);

      const response = await getTransation({
        search: "",
        status: "",
        transactionType: "CLAIMED",
        limit: transactionsData?.result?.total,
      }).unwrap();

      // const data: Transaction[] = response?.result?.data || [];

      if (response.result?.docs.length === 0) {
        toast.info("No data available to export");
        return;
      }

      // Optional: map headers to user-friendly names
      const exportData = response?.result?.docs.map((row) => ({
        ID: row._id,
        "Transaction Type": row.transactionType,
        Hash: row.hash,
        From: row.from,
        "Wallet Address": row.walletAddress,
        Amount: row.amount,
        Status: row.status,
        "Total Reward": row.totalReward,
        "All Tasks": row.allTasks,
        "Verified Tasks": row.allVerifiedTasks,
        "Is Claimed": row.isClaimed ? "Yes" : "No",
        "Is Completed": row.isCompleted ? "Yes" : "No",
        "Created At": new Date(row.createdAt).toLocaleString(),
      }));

      // Convert JSON → worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData!);

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      saveAs(blob, `transactions_${Date.now()}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = (fetchError?.data as ApiError) || {};
      toast.error(errorData?.responseMessage || "Error exporting file");
    } finally {
      setIsExporting(false);
    }
  };

  const totalClaimed = claimHistory
    .filter((claim) => claim.status === "completed")
    .reduce((sum, claim) => sum + parseInt(claim.amount), 0);

  const pendingClaims = claimHistory
    .filter(
      (claim) => claim.status === "pending" || claim.status === "processing",
    )
    .reduce((sum, claim) => sum + parseInt(claim.amount), 0);

  // useEffect(() => {
  //   // Connect and listen for updates
  //   socket.connect();

  //   socket.on("campaignUpdate", (updatedCampaign) => {
  //     // setCampaigns((prevCampaigns) =>
  //     //   prevCampaigns.map((campaign) =>
  //     //     campaign.id === updatedCampaign.id
  //     //       ? { ...campaign, ...updatedCampaign }
  //     //       : campaign
  //     //   )
  //     // );
  //   });

  //   // Clean up on unmount
  //   return () => {
  //     socket.off("campaignUpdate");
  //     socket.disconnect();
  //   };
  // }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader size="lg" text="Loading claim history..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl text-white mb-2">
            Claim History
          </h1>
          <p className="text-gray-400">
            Track all your BINGOLD token claims and transactions
          </p>
        </div>

        <Button
          disabled={isExporting}
          onClick={exportCSV}
          variant="outline"
          className="border-yellow-500 cursor-pointer text-yellow-400 hover:bg-yellow-500 hover:text-black mt-4 md:mt-0 flex items-center gap-2.5"
        >
          <Download className="w-4 h-4 mr-2" />
          Export History{" "}
          {isExporting && (
            <div className="animate-spin rounded-full size-6 border-b-2 border-yellow-500 mr-2"></div>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-400 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.totalClaimed}
                </div>
                <div className="text-gray-400 text-sm">Total Claimed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.totalPendingClaim}
                </div>
                <div className="text-gray-400 text-sm">Pending Claims</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.activeCampaigns}
                </div>
                <div className="text-gray-400 text-sm">Total Claims</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-gray-900/50 border-yellow-500/20">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {/* <Button
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* Claims List */}
      <div className="space-y-4">
        {!isError &&
          transactionsData?.result?.docs &&
          transactionsData?.result?.docs?.length > 0 &&
          transactionsData?.result?.docs.map((claim) => {
            const StatusIcon = getStatusInfo(claim.status).icon;
            const statusInfo = getStatusInfo(claim.status);

            return (
              <Card
                key={claim._id}
                className="bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-colors"
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
                    <div className="flex items-start space-x-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${statusInfo.bgColor}`}
                      >
                        <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold mb-1">
                          {claim.campaignId?.name}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <span>
                            {new Date(claim.createdAt).toLocaleDateString()}
                          </span>
                          {claim.createdAt && (
                            <span>
                              {new Date(claim.createdAt).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                        {claim.hash && (
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="text-xs text-gray-500">TX:</span>
                            <code className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                              {/* {claim.txHash.slice(0, 10)}...
                              {claim.txHash.slice(-8)} */}
                              {claim.hash}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-gray-400 hover:text-white p-1 h-auto"
                              onClick={() =>
                                navigator.clipboard.writeText(claim.hash!)
                              }
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-yellow-400 font-semibold text-lg">
                          {claim.amount} BINGOLD
                        </div>
                        <Badge
                          className={`${statusInfo.bgColor} ${statusInfo.color} border-0`}
                        >
                          {statusInfo.text}
                        </Badge>
                      </div>

                      {claim.hash && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          onClick={() =>
                            window.open(
                              `https://bscscan.com/tx/${claim.hash}`,
                              "_blank",
                            )
                          }
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

        {(isError || transactionsData?.result?.docs?.length === 0) && (
          <div className="text-center">
            <p className="text-gray-400">No claims found.</p>
          </div>
        )}
      </div>

      {/* Load More */}
      {/* <div className="text-center">
        <Button
          variant="outline"
          className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black"
        >
          Load More Claims
        </Button>
      </div> */}
    </div>
  );
}
